<template>
<article>
    <p>
        In addition to built-in notification integrations like
        <router-link :to="{params: {item: 'slack'}}">Slack</router-link>,
        <router-link :to="{params: {item: 'teams'}}">Microsoft Teams</router-link>,
        <router-link :to="{params: {item: 'pagerduty'}}">Pagerduty</router-link>, and
        <router-link :to="{params: {item: 'opsgenie'}}">Opsgenie</router-link>,
        Coroot can integrate with nearly any system using Webhooks.
    </p>
    <p>To configure a Webhook integration:</p>
    <ul>
        <li>Go to the project integration settings <v-icon>mdi-cog</v-icon> &rarr; <b>Integrations</b></li>
        <li>Create a Webhook integration</li>
        <li>
            Paste a Webhook URL to the form
            <v-card max-width="800px" class="text-center pa-2">
                <v-img eager class="ma-auto" src="/static/img/docs/coroot-ce/webhook-integration.png" />
            </v-card>
        </li>
        <li>
            Configure HTTP basic authentication and headers if required.
        </li>
        <li>
            Define templates for incidents and deployments.
        </li>
        <li>
            Send a test alert to check the integration.
        </li>
    </ul>

    <h2>Template data</h2>
    <p>
        Notification templates are based on the <a href="https://golang.org/pkg/text/template" target="_blank">Go templating</a> system.
        Coroot applies provided templates to the following data structures:
    </p>
    <pre v-hl.go>
type IncidentTemplateValues struct {
	Status string // OK, WARNING, CRITICAL
	Application struct {
        Namespace string
        Kind      string
        Name      string
    }
	Reports     []struct {
        Name    string // SLO, CPU, Memory, Net, ...
        Check   string // Availability, Latency, Memory leak, ...
        Message string // "error budget burn rate is 26x within 1 hour", "app containers have been restarted 11 times by the OOM killer", ...
    }
	URL string // backlink to the incident page
}
    </pre>
    <pre v-hl.go>
type DeploymentTemplateValues struct {
	Status string // Deployed, Cancelled, Stuck
	Application struct {
        Namespace string
        Kind      string
        Name      string
    }
    Version string   // deployed application version
    Summary []string // "Availability: 87% (objective: 99%)", "CPU usage: +21% (+$37/mo)", "Memory: a memory leak detected", ...
	URL string       // backlink to the deployment page
}
    </pre>

    <h2>Examples</h2>

    <v-tabs class="mb-4">
        <v-tab :to="{hash: 'text'}">Plain text</v-tab>
        <v-tab :to="{hash: 'json'}">JSON</v-tab>
        <v-tab :to="{hash: 'telegram'}">Telegram</v-tab>
        <v-tab :to="{hash: 'mattermost'}">Mattermost</v-tab>
        <v-tab :to="{hash: 'discord'}">Discord</v-tab>
    </v-tabs>

    <div id="text" :class="{hide: tab !== 'text'}">
        <p>
            If you want to customize the message format, you can utilize <a href="https://golang.org/pkg/text/template" target="_blank">Go templating</a> syntax
            to modify alert or deployment notifications based on the provided data structure.
            Below are sample templates you can use as examples.
        </p>
        <p>Incident template:</p>
        <pre v-hl><template v-pre>
{{- if eq .Status `OK` }}
{{ .Application.Name }}@{{ .Application.Namespace }} incident resolved
{{- else }}
{{ .Status }} {{ .Application.Name }}@{{ .Application.Namespace }} is not meeting its SLOs
{{- end }}
{{- range .Reports }}
• *{{ .Name }}* / {{ .Check }}: {{ .Message }}
{{- end }}
{{ .URL }}
        </template></pre>

        <p>Deployment template:</p>
        <pre v-hl><template v-pre>
Deployment of {{ .Application.Name }}@{{ .Application.Namespace }}
*Status*: {{ .Status }}
*Version*: {{ .Version }}
{{- if .Summary }}
*Summary*:
{{- range .Summary }}
• {{ . }}
{{- end }}
{{- end }}
{{ .URL }}
        </template></pre>
    </div>

    <div id="json" :class="{hide: tab !== 'json'}">
        <p>
            If the system you aim to integrate accepts JSON-formatted messages, you can employ the built-in <var>json</var> template function:
        </p>
        <pre v-hl><template v-pre>
{{ json . }}
        </template></pre>
        <p>
            This template will encode the incident and deployment data structures into valid JSON messages with the specified schema.
        </p>
        <p>A sample of resulting incident message:</p>
        <pre v-hl.json>
{
    "status": "WARNING",
    "application": "default:Deployment:app1",
    "reports": [
        {
            "name": "SLO",
            "check": "Latency",
            "message": "error budget burn rate is 20x within 1 hour"
        },
        {
            "name": "Net",
            "check": "Network round-trip time (RTT)",
            "message": "high network latency to 2 upstream services"
        }
    ],
    "url": "http://127.0.0.1:8080/p/x0xwl4jz/app/default:Deployment:app1?incident=123ab456"
}
        </pre>
        <p>A sample of resulting deployment message:</p>
        <pre v-hl.json>
{
    "status": "Deployed",
    "application": "default:Deployment:app1",
    "version": "123ab456: app:v1.8.2",
    "summary": [
        "💔 Availability: 87% (objective: 99%)",
        "💔 CPU usage: +21% (+$37/mo) compared to the previous deployment",
        "🎉 Memory: looks like the memory leak has been fixed"
    ],
    "url": "http://127.0.0.1:8080/p/x0xwl4jz/app/default:Deployment:app1/Deployments#123ab456:123"
}
        </pre>
    </div>

    <div id="telegram" :class="{hide: tab !== 'telegram'}">
        <p>
            Telegram's <a href="https://core.telegram.org/bots/api#sendmessage" target="_blank">sendMessage</a> API call
            expects JSON with two required fields: <var>chat_id</var> and <var>text</var>.
        </p>
        <p>
            Follow the steps below to create a Telegram Bot and obtain <var>chat_id</var>:
        </p>
        <ol>
            <li>Create a Telegram Bot:
                <ul>
                    <li>Open Telegram and search for the <var>BotFather</var> user.</li>
                    <li>Start a chat with BotFather and type <var>/newbot</var>.</li>
                    <li>Follow the instructions to set up your new bot. You'll need to provide a name and a username for your bot.</li>
                    <li>Once the bot is created, BotFather will provide you with a token. Keep this token safe as you'll need it later.</li>
                </ul>
            </li>
            <li>Add Your Bot to a Chat:
                <ul>
                    <li>After creating your bot, you'll want to add it to a chat. You can add it to an existing group or create a new one.</li>
                    <li>In Telegram, search for the chat where you want to add the bot.</li>
                    <li>Click on the chat name to open the chat settings.</li>
                    <li>Select "Add members" or "Invite to group" depending on your platform.</li>
                    <li>Search for your bot's username and add it to the chat.</li>
                </ul>
            </li>
            <li>Obtain Chat ID:
                <ul>
                    <li>Once your bot is added to the chat, you need to obtain the chat ID.</li>
                    <li>Send a message to the chat where your bot is added.</li>
                    <li>Now, you need to fetch the chat ID. You can do this using various methods:</li>
                    <li>Use a Telegram bot like <var>@userinfobot</var> or <var>@getidsbot</var>. Send the command <var>/getid</var> in the chat and it will reply with the chat ID.</li>
                </ul>
            </li>
        </ol>
        <p>Sample incident template:</p>
        <pre v-hl.json><template v-pre>
{
    "chat_id": "-123456789",
    "text": "
{{- if eq .Status `OK` }}
[{{ .Application.Name }}@{{ .Application.Namespace }}]({{ .URL }}) incident resolved
{{- else }}
{{ .Status }} [{{ .Application.Name }}@{{ .Application.Namespace }}]({{ .URL }}) is not meeting its SLOs
{{- end }}
{{- range .Reports }}
• *{{ .Name }}* / {{ .Check }}: {{ .Message }}
{{- end }}",
    "parse_mode": "Markdown"
}
        </template></pre>

        <p>Sample deployment template:</p>
        <pre v-hl.json><template v-pre>
{
    "chat_id": "-123456789",
    "text": "
Deployment of [{{ .Application.Name }}@{{ .Application.Namespace }}]({{ .URL }})
*Status*: {{ .Status }}
*Version*: {{ .Version }}
{{- if .Summary }}
*Summary*:
{{- range .Summary }}
• {{ . }}
{{- end }}
{{- end }}",
    "parse_mode": "Markdown"
}
        </template></pre>
    </div>

    <div id="mattermost" :class="{hide: tab !== 'mattermost'}">
        <p>
            Mattermost's Incoming Webhook requires JSON with the <var>text</var> or <var>attachments</var> fields specified.
            Refer to the Mattermost <a href="https://developers.mattermost.com/integrate/webhooks/incoming/" target="_blank">documentation</a>
            to create an incoming webhook and obtain its URL.
        </p>
        <p>Sample incident template:</p>
        <pre v-hl.json><template v-pre>
{
    "text": "
{{- if eq .Status `OK` }}
#### [{{ .Application.Name }}@{{ .Application.Namespace }}]({{ .URL }}) incident resolved
{{- else }}
#### {{ .Status }} [{{ .Application.Name }}@{{ .Application.Namespace }}]({{ .URL }}) is not meeting its SLOs
{{- end }}
{{- range .Reports }}
• **{{ .Name }}** / {{ .Check }}: {{ .Message }}
{{- end }}"
}
        </template></pre>

        <p>Sample deployment template:</p>
        <pre v-hl.json><template v-pre>
{
    "text": "
#### Deployment of [{{ .Application.Name }}@{{ .Application.Namespace }}]({{ .URL }})
**Status**: {{ .Status }}
**Version**: {{ .Version }}
{{- if .Summary }}
**Summary**:
{{- range .Summary }}
• {{ . }}
{{- end }}
{{- end }}"
}
        </template></pre>
    </div>

    <div id="discord" :class="{hide: tab !== 'discord'}">
        <p>
            Discord's Webhook requires JSON with the <var>content</var> field specified (<a href="https://discord.com/developers/docs/resources/webhook#execute-webhook" target="_blank">doc</a>).
            Refer to the Discord <a href="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks" target="_blank">documentation</a>
            to create a webhook and obtain its URL.
        </p>
        <p>Sample incident template:</p>
        <pre v-hl.json><template v-pre>
{
    "content": "
{{- if eq .Status `OK` }}
### [{{ .Application.Name }}@{{ .Application.Namespace }}]({{ .URL }}) incident resolved
{{- else }}
### {{ .Status }} [{{ .Application.Name }}@{{ .Application.Namespace }}]({{ .URL }}) is not meeting its SLOs
{{- end }}
{{- range .Reports }}
• **{{ .Name }}** / {{ .Check }}: {{ .Message }}
{{- end }}"
}
        </template></pre>

        <p>Sample deployment template:</p>
        <pre v-hl.json><template v-pre>
{
    "content": "
### Deployment of [{{ .Application.Name }}@{{ .Application.Namespace }}]({{ .URL }})
**Status**: {{ .Status }}
**Version**: {{ .Version }}
{{- if .Summary }}
**Summary**:
{{- range .Summary }}
• {{ . }}
{{- end }}
{{- end }}"
}
        </template></pre>
    </div>

</article>
</template>

<script>
export default {
    computed: {
        tab() {
            const hash = this.$route.hash;
            return hash ? hash.replace('#', '') : 'text';
        },
    },
    methods: {
        class(t) {
            return {hide: this.tab !== t};
        }
    },
}
</script>

<style scoped>
.hide {
    display: none;
}
</style>
